import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { ZorroTableComponent } from '../custom-table/zorro-table.component';
import { ExpandableAccordionComponent } from '../expandable-accordion/expandable-accordion.component';
import { LoadingComponent } from '../loading/loading.component';
import { RestService } from '../../../core/services/rest.service';
import { ToasterService } from '../../../core/services/toaster-service';
import { MessageEnum } from '../../../core/utils/message.enum';

@Component({
  selector: 'app-high-impact-details',
  standalone: true,
  imports: [CommonModule, NzTabsModule, ZorroTableComponent, ExpandableAccordionComponent, LoadingComponent],
  templateUrl: './high-impact-details.component.html',
  styleUrl: './high-impact-details.component.scss'
})
export class HighImpactDetailsComponent implements OnInit {
  /**
   * Combined data object containing:
   *  - impactedTestCases: test case rows (testCaseID, testScenario, methodName, sourceCodePath)
   *  - modifiedFiles:     code file rows (codeSnippetId, sourceCodePath, recommendations[])
   */
  @Input() impactedTestCaseData: any;
  @Input() type: 'impacted' | 'most_impacted' = 'impacted';
  @Input() mappingId: any = '';
  @Input() reportName: string = '';
  @Output() actionEmit = new EventEmitter<any>();

  activeTab = 0;
  loading = false;
  private _modifiedFilesData: any[] = [];

  constructor(private restService: RestService, private readonly toaster: ToasterService) {}

  readonly tableColumns = [
    {
      key: 'testCase',
      display_name: 'Test Case',
      headerStyles: {
        background: '#1E2235',
        color: '#FFFFFF',
        fontSize: '13px',
        fontWeight: '600'
      },
      _config: {
        type: 'double-line-data',
        targetKey: {
          firstLine: 'testCaseId',
          secondLine: 'testScenario'
        }
      }
    },
    {
      key: 'method',
      display_name: 'Method & Source Code Path',
      headerStyles: {
        background: '#1E2235',
        color: '#FFFFFF',
        fontSize: '13px',
        fontWeight: '600'
      },
      _config: {
        type: 'double-line-data',
        style: { gap: '8px' },
        prefixImage: {
          isStatic: true,
          url: 'assets/images/virtusa-icons/code.png',
          style: { width: '16px', marginRight: '6px', marginLeft: '2px' }
        },
        suffixImage: {
          isStatic: true,
          url: 'assets/images/virtusa-icons/report-gray.png',
          style: { height: '14px', width: '12px', marginRight: '12px', marginLeft: '2px' }
        },
        targetKey: {
          firstLine: 'methodName',
          secondLine: 'sourceCodePath'
        }
      }
    },
    {
        key: 'action',
      display_name: 'Actions',
      headerStyles: {
        background: '#1E2235',
        color: '#FFFFFF',
        fontSize: '13px',
        fontWeight: '600',
        width: '120px'
      },
      _config: {
        type: 'link',
        name: 'View Details',
        actionKey: 'mapping_view'
      }
    }
  ];

  ngOnInit(): void {
    if(this.type === 'most_impacted') {
      this.getGeneratedMostImpactedTestcases();
    } else {
      this.getGeneratedImpactedTestcases();
    }
  }

  getGeneratedImpactedTestcases(): void {
    this.loading = true;
    this.restService.getGeneratedImpactedTestcases(this.mappingId).subscribe({
      next: (item: any) => {
        this._modifiedFilesData = item?.data || [];
        this.loading = false;
      },
      error: (err: any) => {
        this.toaster.pop({ type: 'error', text: MessageEnum.IMPACTED_TESTCASES_LOAD_FAILURE });
        console.error('Error loading modified files:', err);
        this.loading = false;
      }
    });
  }

  getGeneratedMostImpactedTestcases(): void {
    this.loading = true;
    this.restService.getMostImpactedTestcases(this.mappingId, this.reportName).subscribe({
      next: (item: any) => {
        this._modifiedFilesData = item?.data || [];
        this.loading = false;
      },
      error: (err: any) => {
        this.toaster.pop({ type: 'error', text: MessageEnum.MOST_IMPACTED_TESTCASES_LOAD_FAILURE });
        console.error('Error loading most impacted testcases:', err);
        this.loading = false;
      }
    });
  }

  get accordionData(): any {
    return {
      rows: this._modifiedFilesData,
      columns: [
        { mapping_key: 'sourcecodepath', display_name: 'Source Code Path', isVisible: true }
      ]
    };
  }

  get impactedTestCases(): any[] {
    return this.impactedTestCaseData || [];
  }

  get modifiedFiles(): any[] {
    return this._modifiedFilesData;
  }

  onTableAction(event: any): void {
    this.actionEmit.emit({ action: event });
    this.actionEmit.emit(event);
  }

  onAccordionAction(event: any): void {
    this.actionEmit.emit(event);
  }
}
